/**
 * @fileoverview Component for displaying a reset password form.
 * This component includes a form for resetting a password and displays a message if the password is incorrect or duplicated.
 */

import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import NovoicDashboardLogo from '../../assets/novoic-dashboard-logo.svg'
import { useRequestRecoveryMutation } from '../../services/users'
import { ResetPasswordForm } from './ResetPasswordForm'

/**
 * @function ResetPassword
 * @description Component for displaying a reset password form.
 * @returns {JSX.Element} - A React component that renders the reset password form.
 */
export default function ResetPassword() {
    const [requestRecovery, { isSuccess }] = useRequestRecoveryMutation()
    const [error, setError] = useState<Error | null>(null)
    const [isPending, setIsPending] = useState<boolean>(false)

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsPending(true)

        const formData = new FormData(e.currentTarget)
        const payload: Record<string, string> = {}

        for (const [key, value] of formData.entries()) {
            payload[key] = value as string
        }

        try {
            requestRecovery(payload as Record<'username', string>)
        } catch (e: unknown) {
            setError(e as Error)
        } finally {
            setIsPending(false)
        }
    }

    return (
        <div className="nv-grid min-h-screen">
            <div className="md-container max-h-full items-center content-center justify-center col-start-1 md:col-start-2 lg:col-start-5">
                <div className="w-full max-w-md space-y-8">
                    <div className="flex flex-col items-center gap-y-4">
                        <NovoicDashboardLogo className="w-56" />
                        <h2 className="text-center text-3xl font-bold font-serif text-gray-900">
                            Reset password
                        </h2>

                        <p className="text-gray-600 font-regular text-center">
                            To reset your password, please provide the email address associated with
                            your Novoic Dashboard account.
                        </p>
                    </div>
                    {!isSuccess ? (
                        <ResetPasswordForm onSubmit={onSubmit} isPending={isPending} />
                    ) : (
                        <div className="rounded-md bg-indigo-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <EnvelopeIcon
                                        className="h-5 w-5 text-indigo-400"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div className="ml-3 flex-1 md:flex md:justify-between">
                                    <p className="text-sm text-indigo-800 font-medium">
                                        We have sent a password reset link to the email address you
                                        provided. To reset your password, please check your email,
                                        click on the link, and follow the instructions.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-center items-center">
                        <NavLink
                            to="/login"
                            className="inline-flex gap-x-1 px-2 py-1 rounded-md transition-all text-indigo-600 hover:bg-indigo-50"
                        >
                            <ArrowLeftIcon className="w-5 h-5 stroke-indigo-600" />
                            <span className="font-medium text-sm">Back to Log In</span>
                        </NavLink>
                    </div>
                </div>
            </div>
        </div>
    )
}
